"use client";

import MuxPlayerReact from "@mux/mux-player-react/lazy";
import { Play } from "iconoir-react";
import dynamic from "next/dynamic";
import { useEffect, useState } from "react";
import type { CSSProperties, SyntheticEvent } from "react";

import { Dialog, DialogClose, DialogTitle } from "@/components/ui/dialog";
import {
  VideoDialogPopup,
  videoDialogCloseClass,
} from "@/components/ui/video-dialog";
import { cn } from "@/lib/utils";

const MuxPlayer = dynamic(
  () =>
    import("@/components/post-rich-text/mux-player").then(
      (module) => module.MuxPlayer
    ),
  { ssr: false }
);

// The lazy player's JSX type exceeds tsgo's union limit — see mux-player.tsx.
const BackgroundPlayer = MuxPlayerReact as unknown as (
  props: Record<string, unknown>
) => React.JSX.Element;

const BACKGROUND_PLAYER_STYLE = {
  "--controls": "none",
  "--media-object-fit": "cover",
  "--media-object-position": "50% 0%",
  width: "100%",
  height: "100%",
} as CSSProperties;

interface HeroBackgroundVideoProps {
  playbackId: string;
  /**
   * Loop window within the source video, in seconds. Without `start` the whole
   * video loops; without `end` it plays from `start` to the end, then restarts.
   */
  start?: number;
  end?: number;
  /** Loop window for viewports below the `lg` breakpoint (portrait crops). */
  mobile?: { start?: number; end?: number };
  className?: string;
}

// Same breakpoint the hero layout switches at (Tailwind `lg`).
const DESKTOP_QUERY = "(min-width: 1056px)";

/**
 * Muted, looping snippet that fades in over the poster once it is playing.
 * Skipped for reduced-motion and data-saver visitors, who keep the poster.
 * The loop window is picked once, when the video starts, from the viewport.
 */
export function HeroBackgroundVideo({
  playbackId,
  start: desktopStart,
  end: desktopEnd,
  mobile,
  className,
}: HeroBackgroundVideoProps) {
  const [loopWindow, setWindow] = useState<{ start?: number; end?: number }>({
    start: desktopStart,
    end: desktopEnd,
  });
  const { start, end } = loopWindow;
  const mobileStart = mobile?.start;
  const mobileEnd = mobile?.end;
  const hasEnd = end !== undefined;
  const loopsNatively = start === undefined;
  const [enabled, setEnabled] = useState(false);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    const saveData = (
      navigator as Navigator & { connection?: { saveData?: boolean } }
    ).connection?.saveData;
    if (reducedMotion || saveData) return;

    // Let the poster paint first; start the video once the page is idle.
    const enable = () => {
      if (
        mobileStart !== undefined &&
        !window.matchMedia(DESKTOP_QUERY).matches
      ) {
        setWindow({ start: mobileStart, end: mobileEnd });
      }
      setEnabled(true);
    };
    if (typeof window.requestIdleCallback === "function") {
      const id = window.requestIdleCallback(enable, { timeout: 1500 });
      return () => window.cancelIdleCallback(id);
    }
    const id = globalThis.setTimeout(enable, 600);
    return () => globalThis.clearTimeout(id);
  }, [mobileStart, mobileEnd]);

  if (!enabled) return null;

  return (
    <div
      aria-hidden
      className={cn(
        "pointer-events-none absolute inset-0 transition-opacity duration-700 motion-reduce:transition-none",
        playing ? "opacity-100" : "opacity-0",
        className
      )}
    >
      <BackgroundPlayer
        playbackId={playbackId}
        streamType="on-demand"
        autoPlay="muted"
        muted
        playsInline
        nohotkeys
        disableCookies
        loading="page"
        preload="auto"
        maxResolution="720p"
        startTime={start}
        loop={loopsNatively}
        style={BACKGROUND_PLAYER_STYLE}
        onPlaying={() => setPlaying(true)}
        onTimeUpdate={(event: SyntheticEvent<HTMLMediaElement>) => {
          if (start === undefined || end === undefined) return;
          const media = event.currentTarget;
          if (media.currentTime >= end) media.currentTime = start;
        }}
        onEnded={(event: SyntheticEvent<HTMLMediaElement>) => {
          if (start === undefined || hasEnd) return;
          const media = event.currentTarget;
          media.currentTime = start;
          void media.play();
        }}
      />
    </div>
  );
}

interface HeroWatchButtonProps {
  label: string;
  playbackId: string;
  title: string;
}

/** "Watch now" trigger that opens the full video in a centred dialog. */
export function HeroWatchButton({
  label,
  playbackId,
  title,
}: HeroWatchButtonProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="group text-ds-body-sm text-grey-0 inline-flex items-center gap-3"
      >
        {label}
        <span className="bg-grey-0/20 border-grey-0/20 rounded-ds-m group-hover:bg-grey-0/30 group-focus-visible:bg-grey-0/30 relative flex size-10 items-center justify-center border transition-[background-color,transform] duration-300 ease-out group-hover:scale-110 group-focus-visible:scale-110 motion-reduce:transition-none">
          <span
            aria-hidden
            className="border-grey-0/40 rounded-ds-m absolute inset-0 animate-[play-ring_2.4s_ease-out_infinite] border motion-reduce:animate-none"
          />
          <Play
            className="size-4 transition-transform duration-300 ease-out group-hover:scale-110 group-focus-visible:scale-110 motion-reduce:transition-none [&_path]:transition-[fill] [&_path]:duration-300 group-hover:[&_path]:fill-white"
            aria-hidden
          />
        </span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <VideoDialogPopup initialFocus={false}>
          <DialogTitle className="sr-only">{title}</DialogTitle>
          <div className="rounded-container relative aspect-video overflow-hidden">
            {open ? (
              <MuxPlayer
                key={playbackId}
                playbackId={playbackId}
                title={title}
                autoPlay="any"
                loading="page"
                preload="auto"
                renditionOrder="desc"
                minResolution="1080p"
                className="absolute inset-0"
              />
            ) : null}
          </div>
          <DialogClose className={videoDialogCloseClass} />
        </VideoDialogPopup>
      </Dialog>
    </>
  );
}
